export interface SourceInformation {
    cu?: string;
    lu?: string;
    ru?: string;
    du?: string;
    cnu?: string;
}
const keyPrefix = "tracker_";
const name = "__utmz";
const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN as string;

export const GlobalScope = (() => {
    if (typeof globalThis !== "undefined") {
        return globalThis;
    }
    if (typeof window !== "undefined") {
        return window;
    }
    if (typeof self !== "undefined") {
        return self;
    }
    if (typeof global !== "undefined") {
        return global;
    }
})();

const getCookie = (name: string) => {
    try {
        const cookieArray = document.cookie.split(";");
        const cookieName = `${name}=`;

        for (let cookie of cookieArray) {
            cookie = cookie.trim();
            if (cookie.indexOf(cookieName) === 0) return cookie.substring(cookieName.length);
        }
        return null;
    } catch (e) {
        return null;
    }
};

const setCookieStorage = (name: string, value: any, expirationDays?: number) => {
    if (!Object.keys(value).length) {
        return;
    }

    const expireData = new Date(new Date().getTime() + (expirationDays ?? 30) * 60 * 24 * 60 * 1000);

    if (!cookieDomain) {
        console.warn("Cookie domain is not set");
        return;
    }
    document.cookie = `${keyPrefix + name}=${JSON.stringify(value)};expires=${expireData};domain=${cookieDomain};path=/`;
};

export const getUtmAndReferrer = (): SourceInformation => {
    try {
        const attr = JSON.parse(getCookie(keyPrefix + name) ?? "{}");
        const duAttr = JSON.parse(getCookie(keyPrefix + name + "_du") ?? "{}");
        return { ...duAttr, ...attr };
    } catch (error) {
        console.log("getUtmAndReferrer::error::", JSON.stringify(error));
        console.log("getUtmAndReferrer::cookie", getCookie(keyPrefix + name));
    }
    return {};
};

const _getLocationPathWithQuery = () => {
    const { pathname, search } = GlobalScope?.location || {};
    return `${pathname ?? ""}${search ?? ""}`;
};

const _getReferrer = () => {
    const ref = document?.referrer ?? "";
    if (ref) {
        try {
            const referrerUrl = new URL(ref);
            const isGallabox = referrerUrl.hostname.toLowerCase() === "gallabox.com" || referrerUrl.hostname.toLowerCase() === "app.gallabox.com";
            if (isGallabox) return "";
        } catch (e) {
            console.error(e);
        }
    }
    return ref;
};

export const getAffiliateId = (): string | undefined => {
    const val = getCookie("tap_ref_code");
    return val ? decodeURIComponent(val) : undefined;
};

export const refTracker = (): void => {
    try {
        const params = new URLSearchParams(GlobalScope?.location?.search ?? "");
        const ref = params.get("ref");
        if (!ref) return;

        const expireDate = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000);
        if (!cookieDomain) return;
        document.cookie = `tap_ref_code=${encodeURIComponent(ref)};expires=${expireDate};domain=${cookieDomain};path=/`;
    } catch (e) {
        console.error("refTracker::error", e);
    }
};

export const urlTracker = (): void => {
    const previousValues = getUtmAndReferrer();
    const currentPathName = _getLocationPathWithQuery();
    const referrerUrl = _getReferrer();

    const lu = previousValues?.lu ?? currentPathName;

    const currentAttributes = {
        cu: currentPathName,
        lu,
        ru: previousValues?.ru ?? referrerUrl,
        cnu: currentPathName,
    };
    setCookieStorage(name, currentAttributes, 1);

    if (previousValues?.du) return;

    const discoveryAttributes = {
        du: lu,
    };
    setCookieStorage(name + "_du", discoveryAttributes, 30);
};

const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export const buildCalendlyUrl = (baseUrl: string, affiliateId?: string | null, utmData?: Record<string, string>): string => {
    const calParams = new URLSearchParams();

    // Extract existing params from baseUrl
    const baseUrlObj = typeof window !== "undefined" ? new URL(baseUrl, window.location.origin) : new URL(baseUrl, "https://gallabox.com");
    const baseParams = new URLSearchParams(baseUrlObj.search);
    const baseUrlOnly = baseUrl.split("?")[0];

    // Get page params from current URL
    const pageParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();

    // First, preserve all non-UTM params from baseUrl
    baseParams.forEach((val, key) => {
        if (!UTM_FIELDS.includes(key as any)) {
            calParams.set(key, val);
        }
    });

    // Then add UTM params: page params take precedence, then utmData, then baseUrl params
    UTM_FIELDS.forEach((key) => {
        const val = pageParams.get(key) || utmData?.[key] || baseParams.get(key);
        if (val && val.trim() !== "") {
            calParams.set(key, val);
        }
    });

    if (affiliateId) {
        const existing = calParams.get("utm_content") || "";
        let contentObj: Record<string, string> = {};

        if (existing) {
            try {
                const parsed = JSON.parse(existing);
                contentObj = parsed && typeof parsed === "object" ? parsed : { content: existing };
            } catch {
                contentObj = { content: existing };
            }
        }

        contentObj.affiliateId = affiliateId;
        calParams.set("utm_content", JSON.stringify(contentObj));
    }

    const query = calParams.toString();
    return query ? `${baseUrlOnly}?${query}` : baseUrlOnly;
};

export const buildZohoFormUrl = (baseUrl: string, utmData: Record<string, string>, affiliateId?: string | null, customParams?: Record<string, string>): string => {
    const params = new URLSearchParams();

    // Add UTM params (including utm_content)
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((key) => {
        const val = utmData[key];
        if (val && val.trim() !== "") {
            params.set(key, val);
        }
    });

    // Add custom params (e.g., webinar_name, ebook_name) - trim whitespace consistently
    if (customParams) {
        Object.entries(customParams).forEach(([key, val]) => {
            if (val && val.trim() !== "") {
                params.set(key, val);
            }
        });
    }

    // Add affiliateId
    if (affiliateId) {
        params.set("affiliate_id", affiliateId);
    }

    const query = params.toString();
    if (!query) return baseUrl;

    // Check if baseUrl already has query params
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${query}`;
};
